import React, { JSX, useContext, useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import { colors } from "constants/colors";
import { assetsRoot } from "config";
import { LogContext } from "context/log";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";
import { SmartButton } from "components/SmartCommerce";
import { getProductUpc } from "constants/commerce";
import { PRODUCTS } from "./drinks";

import st from "./drinks.module.scss";

// Replica of drinkpoppi.com/collections/variety-packs at the same URL (QA
// parity report: populated browse page live on prod, 404 on staging). Live
// reuses the drinks-collection template — same "we flex on flavor" textPath
// hero, same arch-card grid — scoped to the current packs with a single
// product-count facet chip.

const A = `${assetsRoot}/poppi/collections`;

// live card order on /collections/variety-packs
const PACK_ORDER = [
  "fall-for-flavor",
  "major-mix",
  "functional-favorites",
  "crowd-pleasers",
  "classics",
  "short-list",
  "trend-setters",
];

const PACKS = PACK_ORDER.map((slug) => PRODUCTS.find((p) => p.slug === slug)!);

function HeroArc(): JSX.Element {
  return (
    <svg
      className={st.heroArc}
      viewBox="0 0 1440 450"
      fill="none"
      aria-hidden="true"
    >
      <path
        id="packs-flex-path"
        fill="none"
        d="M0 297.554C460.88 125.568 580.43 320.091 842.94 373.473C1154.19 436.774 1324.8 325.12 1440 230.463"
      />
      <text>
        <textPath href="#packs-flex-path" startOffset="19%">
          we flex on flavor
        </textPath>
      </text>
    </svg>
  );
}

function RibbonWave(): JSX.Element {
  return (
    <svg
      className={st.ribbon}
      viewBox="0 0 1443 476"
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 0H1443V207.366C1327.19 301.34 1155.37 409.172 844.709 346.477C770.022 331.405 706.843 305.058 644.142 278.91C485.702 212.836 330.314 148.035 0 269.642V0Z"
        fill={colors.magenta}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1443 221.287L1443 201.847C1327.6 296.303 1156.2 406.216 844.407 343.055C770.003 327.982 707.063 301.635 644.6 275.487C486.297 209.22 331.052 144.233 6.77313e-05 267.293L0 476H0.000342132C329.868 243.994 593.324 312.566 753.508 354.258C787.916 363.214 817.558 370.929 842.071 374.159C965.217 390.386 1190.18 413.693 1443 221.287Z"
        fill={colors.yellow}
      />
    </svg>
  );
}

export default function VarietyPacksCollection(): JSX.Element {
  const { useLogPageView } = useContext(LogContext);

  useEffect(() => {
    const body = document.body;
    body.style.setProperty("--color-primary", colors.magenta);
    body.style.setProperty("--logo-color", "#ffffff");
    body.style.setProperty("--header-shadow", `0 4px 32px ${colors.yellow}`);
    body.style.setProperty("--page-top", "0px");
    body.style.setProperty("--footer-text", colors.yellow);
    return () => {
      body.style.removeProperty("--color-primary");
      body.style.removeProperty("--logo-color");
      body.style.removeProperty("--header-shadow");
      body.style.removeProperty("--page-top");
      body.style.removeProperty("--footer-text");
    };
  }, []);

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.collection,
    pageCategory: "variety-packs",
    pageTitle: `Variety Packs – ${META_TITLE}`,
    id: generatePageId("collections-variety-packs-page"),
  });

  return (
    <div className={st.page}>
      <Head>
        <title>{`Variety Packs – ${META_TITLE}`}</title>
        <link
          rel="canonical"
          href={`${CANONICAL_URL_BASE}/collections/variety-packs`}
        />
        <meta
          name="description"
          content="we flex on flavor — shop every poppi prebiotic soda variety pack."
          key="description"
        />
      </Head>

      {/* ---------------- HERO (magenta + yellow ribbon + arched title) ---------------- */}
      <section className={st.hero}>
        <div className={st.heroBand} />
        <RibbonWave />
        <HeroArc />
      </section>

      {/* live's single facet for this collection */}
      <div className={st.chips} role="group" aria-label="filter products">
        <span className={`${st.chip} ${st.chipActive}`}>variety pack ({PACKS.length})</span>
      </div>

      {/* ---------------- pack grid ---------------- */}
      <div className={st.grid}>
        {PACKS.map((p) => (
          <div className={st.card} key={p.slug}>
            <Link
              href={`/products/${p.slug}`}
              className={st.cardLink}
              aria-label={`poppi ${p.name}`}
            >
              <span
                className={st.arch}
                style={{
                  background: `linear-gradient(0deg, ${p.from} 0%, ${p.to} 100%)`,
                }}
              />
              <span className={`${st.can} ${st.canPack}`}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={p.img ?? `${A}/${p.slug}.webp`}
                  alt={`poppi ${p.name}`}
                  className={st.packImage}
                  loading="lazy"
                />
              </span>
              <h4 className={st.name} style={{ color: p.to }}>
                {p.name}
              </h4>
            </Link>
            {getProductUpc(p.slug) && (
              <SmartButton
                upc={getProductUpc(p.slug) as string}
                productName={p.name}
                className={st.cardBtn}
                style={
                  {
                    "--card-primary": p.from,
                    "--card-secondary": p.to,
                    "--color-primary": p.from,
                    "--color-secondary": p.to,
                  } as React.CSSProperties
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
